import React, { useState, useEffect } from "react";
import { Card, CardContent, Typography, Table, TableHead, TableRow, TableCell, TableBody, Grid2 } from "@mui/material";
import { Bar, Pie, Line } from "react-chartjs-2";
import "chart.js/auto";
import { fetchStatsData, terminateDeployment } from "../services/api";
import moment from "moment";
import { toast, Bounce } from "react-toastify";
import { getSelectedUserAccountFromLocalStorage } from "../utils/data";
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { Box } from "@mui/material";
import { formatIndianCurrency } from "../utils/currency";

// Utility function to format snake_case keys
const formatKey = (key) => {
    return key
        .replace(/_/g, " ") // Replace underscores with spaces
        .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize the first letter of each word
};


const StatsDashboard = () => {
    // const { funds_allocated, pnl, net_pnl, pnl_percentage, net_pnl_percentage, num_deployments, deployments, brokerage_charges, other_charges } = data.stats;
    const navigate = useNavigate(); // Hook for programmatic navigation

    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [selectedAccount, setSelectedAccount] = useState(getSelectedUserAccountFromLocalStorage());
    const [autoRefreshInterval, setAutoRefreshInterval] = useState(10);

    const showToast = (type, message) => {
        let properties = {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
            transition: Bounce,
        }
        switch (type) {
            case "success":
                toast.success(message, properties);
                break;
            case "error":
                toast.error(message, properties);
                break;
            case "warning":
                toast.warn(message, properties);
                break;
            default:
                toast.info(message, properties);
        }
    }

    useEffect(() => {
        if (selectedAccount?.broker) {
            fetchStats();
        }
    }, [selectedAccount]);

    // Listen for global user account changes
    useEffect(() => {
        const handleUserAccountChange = (event) => {
            setSelectedAccount(event.detail);
        };

        window.addEventListener('userAccountChanged', handleUserAccountChange);
        
        return () => {
            window.removeEventListener('userAccountChanged', handleUserAccountChange);
        };
    }, []);

    // Fetch trades from API
    const fetchStats = () => {
        if (!selectedAccount?.broker) {
            setLoading(false);
            return;
        }
        
        setLoading(true)
        let currentDate = moment().format("YYYY-MM-DD")
        // currentDate = "2025-02-07"
        fetchStatsData(selectedAccount.broker, currentDate)
            .then((response) => {
                const stats = response.data.stats;
                setStats(stats);
                setLoading(false);
            })
            .catch((error) => {
                console.error("Error fetching stats:", error);
                showToast("error", "Error fetching stats!");
                setLoading(false);
            });
    };

    const total_charges = stats?.brokerage_charges + stats?.other_charges;
    const avg_pnl_per_deployment = stats?.net_pnl / stats?.num_deployments;
    // const max_pnl_deployment = stats?.deployments.reduce((max, d) => (d.pnl > max.pnl ? d : max), stats?.deployments[0]);
    // const min_pnl_deployment = stats?.deployments.reduce((min, d) => (d.pnl < min.pnl ? d : min), stats?.deployments[0]);

    const pnlChartData = {
        labels: ["PnL", "Net PnL"],
        datasets: [
            {
                label: "Amount (INR)",
                data: [stats?.pnl, stats?.net_pnl],
                backgroundColor: [stats?.pnl >= 0 ? "#4caf50" : "#f44336", stats?.net_pnl >= 0 ? "#4caf50" : "#f44336"],
            },
        ],
    };

    const chargesData = {
        labels: ["Brokerage Charges", "Other Charges"],
        datasets: [
            {
                data: [stats?.brokerage_charges, stats?.other_charges],
                backgroundColor: ["#ff9800", "#03a9f4"],
            },
        ],
    };

    const deploymentPnlData = {
        labels: stats?.deployments.map((d) => `(${d.id}) ${d.name}`),
        datasets: [
            {
                label: "PnL (INR)",
                data: stats?.deployments.map((d) => d.net_pnl),
                backgroundColor: stats?.deployments.map((d) => (d.net_pnl >= 0 ? "#4caf50" : "#f44336")),
            },
        ],
    };

    const terminate = (deploymentId) => {
        setLoading(true);
        terminateDeployment(deploymentId)
            .then(() => {
                fetchStats();
                showToast("success", "Deployment terminated successfully!");
                setLoading(false);
            })
            .catch((error) => {
                console.error("Error terminating deployment:", error);
                showToast("error", "Error terminating deployment!");
            });
    };

    if (!selectedAccount) {
        return (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px" gap={2}>
                <AccountBalanceWalletIcon color="disabled" sx={{ fontSize: 64 }} />
                <Typography variant="h5" color="text.secondary">
                    No User Account Selected
                </Typography>
                <Typography variant="body1" color="text.secondary" textAlign="center">
                    Please select a user account from the dropdown in the header to view statistics.
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <Grid2 container spacing={2} sx={{ my: 2 }}>
                <Grid2 size={4}>
                    <Card style={{}}>
                        <CardContent style={{ textAlign: "center" }}>
                            <Typography variant="h6">Gross PnL</Typography>
                            <Typography variant="h5" color={stats?.pnl >= 0 ? "#4caf50" : "#f44336"}>{formatIndianCurrency(stats?.pnl)}</Typography>
                        </CardContent>
                    </Card>
                </Grid2>
                <Grid2 size={4}>
                    <Card>
                        <CardContent style={{ textAlign: "center" }}>
                            <Typography variant="h6">Charges</Typography>
                            <Typography variant="h5">{formatIndianCurrency(stats?.pnl - stats?.net_pnl)}</Typography>
                        </CardContent>
                    </Card>
                </Grid2>
                <Grid2 size={4}>
                    <Card>
                        <CardContent style={{ textAlign: "center" }}>
                            <Typography variant="h6">Net PnL</Typography>
                            <Typography variant="h5" color={stats?.net_pnl >= 0 ? "#4caf50" : "#f44336"}>{formatIndianCurrency(stats?.net_pnl)}</Typography>
                        </CardContent>
                    </Card>
                </Grid2>
                <Grid2 size={12}>
                    <Card style={{}}>
                        <CardContent>
                            <Typography variant="h6" align="center" gutterBottom>Deployment-wise PnL</Typography>
                            <Bar data={deploymentPnlData} />
                        </CardContent>
                    </Card>
                </Grid2>
                <Grid2 size={12}>
                    <Card style={{ gridColumn: "span 2" }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Deployments</Typography>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>ID</TableCell>
                                        <TableCell>Name</TableCell>
                                        {/* <TableCell>Broker</TableCell> */}
                                        <TableCell>Status</TableCell>
                                        <TableCell>Funds Allocated</TableCell>
                                        <TableCell>Strategy</TableCell>
                                        {/* <TableCell>PnL</TableCell> */}
                                        <TableCell>PnL (₹)</TableCell>
                                        <TableCell>PnL %</TableCell>
                                        <TableCell>Start At</TableCell>
                                        <TableCell>End At</TableCell>
                                        {/* <TableCell>Action</TableCell> */}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {stats?.deployments.map((deployment) => (
                                        <TableRow key={deployment.id}>
                                            <TableCell style={{ cursor: "pointer", color: "blue" }} onClick={() => navigate(`/deployments/${deployment.id}`)}>{deployment.id}</TableCell>
                                            <TableCell>{deployment.name}</TableCell>
                                            {/* <TableCell>{deployment.broker}</TableCell> */}
                                            <TableCell>{formatKey(deployment.status)}</TableCell>
                                            <TableCell>{formatIndianCurrency(deployment.funds_allocated)}</TableCell>
                                            <TableCell>{deployment.strategy_version_id}</TableCell>
                                            {/* <TableCell style={{ color: deployment.pnl >= 0 ? "green" : "red" }}>
                                        ₹{deployment.pnl.toLocaleString()}
                                    </TableCell> */}
                                            <TableCell style={{ color: deployment.net_pnl >= 0 ? "#4caf50" : "#f44336" }}>
                                                {formatIndianCurrency(deployment.net_pnl)}
                                            </TableCell>
                                            <TableCell style={{ color: deployment.net_pnl >= 0 ? "#4caf50" : "#f44336" }}>{deployment.net_pnl_percentage}%</TableCell>
                                            <TableCell>{moment(deployment.start_timestamp).format("hh:mm")}</TableCell>
                                            <TableCell>{moment(deployment.end_timestamp).format("hh:mm")}</TableCell>
                                            {deployment.status != "ended" && deployment.status != "terminated" && deployment.status != "stop_loss_hit" &&
                                                <TableCell style={{ cursor: "pointer", color: "red" }} onClick={() => terminate(deployment.id)}>Terminate</TableCell>
                                            }
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </Grid2>
            </Grid2>
        </>
    );
};

export default StatsDashboard;
